import React from 'react';
import { Card } from '../ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList } from 'recharts';

// Helper functions
const calculateDeviation = (plan, ist) => {
  const deviation = ((ist - plan) / plan) * 100;
  return deviation.toFixed(1);
};

const calculatePercentage = (value, total) => (value / total) * 100;

const WaveBar = ({ percentage, previousPercentage = 0 }) => {
  return (
    <div className="relative h-6 w-full bg-white rounded-md overflow-hidden border border-gray-200">
      <div className="absolute inset-0 bg-white" />

      <div className="relative h-full">
        <div
          className="absolute inset-y-0 bg-blue-500 transition-all duration-500"
          style={{
            left: `${previousPercentage}%`,
            width: `${percentage}%`
          }}
        >
          <div className="absolute inset-0 flex items-center justify-center text-white text-sm">
            {percentage > 5 && `${percentage.toFixed(1)}%`}
          </div>
        </div>

        {percentage <= 5 && (
          <div className="absolute inset-0 flex items-center px-3 text-gray-600 text-sm">
            {percentage.toFixed(1)}%
          </div>
        )}
      </div>
    </div>
  );
};



// Legacy CostChart component removed - was showing Plan/Ist labels

const CO2Breakdown = ({ data }) => (
  <Card>
    <div className="p-6">
      <h2 className="text-lg font-semibold mb-6">CO₂ Verteilung</h2>

      <div className="flex justify-end mb-4 text-sm text-gray-500">
        <div className="flex space-x-8">
          <span className="w-20 text-right">Plan (t)</span>
          <span className="w-20 text-right">Ist (t)</span>
          <span className="w-20 text-right">Abweichung</span>
        </div>
      </div>

      <div className="space-y-8">
        {data.map((item, categoryIndex) => {
          let cumulativePercentage = 0;

          return (
            <div key={categoryIndex}>
              <div className="text-lg font-medium mb-4">{item.category}</div>
              <div className="space-y-3">
                {item.subCategories.map((sub, subIndex) => {
                  const currentPercentage = calculatePercentage(sub.ist, item.total.ist);
                  const previousPercentage = cumulativePercentage;
                  cumulativePercentage += currentPercentage;

                  return (
                    <div key={subIndex} className="flex items-center justify-between">
                      <div className="flex-1 flex items-center gap-4">
                        <span className="text-sm font-medium w-32">{sub.name}</span>
                        <div className="flex-1">
                          <WaveBar
                            percentage={currentPercentage}
                            previousPercentage={previousPercentage}
                          />
                        </div>
                        <div className="flex space-x-8">
                          <span className="text-sm w-20 text-right">{sub.plan}</span>
                          <span className="text-sm w-20 text-right">{sub.ist}</span>
                          <span className={`text-sm w-20 text-right ${Number(calculateDeviation(sub.plan, sub.ist)) > 0
                            ? 'text-red-500'
                            : Number(calculateDeviation(sub.plan, sub.ist)) < 0
                              ? 'text-green-500'
                              : 'text-gray-500'
                            }`}>
                            {calculateDeviation(sub.plan, sub.ist)}%
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}

                <div className="flex items-center justify-between pt-3 border-t">
                  <div className="flex-1">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Total {item.category}</span>
                      <div className="flex space-x-8">
                        <span className="w-20 text-right font-medium">{item.total.plan}</span>
                        <span className="w-20 text-right font-medium">{item.total.ist}</span>
                        <span className={`w-20 text-right font-medium ${Number(calculateDeviation(item.total.plan, item.total.ist)) > 0
                          ? 'text-red-500'
                          : Number(calculateDeviation(item.total.plan, item.total.ist)) < 0
                            ? 'text-green-500'
                            : 'text-gray-500'
                          }`}>
                          {calculateDeviation(item.total.plan, item.total.ist)}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        <div className="pt-6 border-t">
          <div className="flex justify-between items-center">
            <span className="text-lg font-medium">Gesamt CO₂e</span>
            <div className="flex space-x-8">
              <span className="w-20 text-right font-medium">
                {data.reduce((acc, item) => acc + item.total.plan, 0)}
              </span>
              <span className="w-20 text-right font-medium">
                {data.reduce((acc, item) => acc + item.total.ist, 0)}
              </span>
              <span className={`w-20 text-right font-medium ${Number(calculateDeviation(
                data.reduce((acc, item) => acc + item.total.plan, 0),
                data.reduce((acc, item) => acc + item.total.ist, 0)
              )) > 0
                ? 'text-red-500'
                : Number(calculateDeviation(
                  data.reduce((acc, item) => acc + item.total.plan, 0),
                  data.reduce((acc, item) => acc + item.total.ist, 0)
                )) < 0
                  ? 'text-green-500'
                  : 'text-gray-500'
                }`}>
                {calculateDeviation(
                  data.reduce((acc, item) => acc + item.total.plan, 0),
                  data.reduce((acc, item) => acc + item.total.ist, 0)
                )}%
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Card>
);

// New components for analytics data format
const AnalyticsChart = ({ data, fullHeight = false, title = "Gesamt CO₂e", use1990Data = false, showCombinedData = false, showToggleButton = false, onToggleView, isToggleActive = false, toggleLabel = '1990 Vergleich', useIconToggle = false, customRightHeader = null }) => {
  if (!data || !data.materials || data.materials.length === 0) {
    return (
      <Card className={fullHeight ? "h-full" : ""}>
        <div className="p-6 h-full flex flex-col">
          <h2 className="text-lg font-semibold mb-6">{title}</h2>
          <div className={`${fullHeight ? 'flex-1' : 'h-80'} flex items-center justify-center text-gray-500`}>
            <p>Keine Daten verfügbar</p>
          </div>
        </div>
      </Card>
    );
  }

  // Transform analytics data for chart
  let chartData;

  if (showCombinedData) {
    // Create combined data with both current and 1990 values
    chartData = [];

    data.materials.forEach(material => {
      // Use the material name as-is (it already contains location from backend)
      const materialName = material.name;

      // Add 1990 data entry first (if available)
      if (material.has1990Data) {
        const materialName1990 = `${materialName} 1990`;
        chartData.push({
          name: materialName1990,
          fullName: materialName1990,
          A1: material.A1_1990 || 0,
          A2: material.A2_1990 || 0,
          A3: material.A3_1990 || 0,
          A4: material.A4_1990 || 0,
          total: material.total_1990 || 0,
          location: material.location,
          type: '1990'
        });
      }

      // Add current data entry
      chartData.push({
        name: materialName,
        fullName: materialName,
        A1: material.A1,
        A2: material.A2,
        A3: material.A3,
        A4: material.A4 || 0,
        total: material.total,
        location: material.location,
        type: 'current'
      });
    });
  } else {
    // Original single data format
    chartData = data.materials
      .filter(material => use1990Data ? material.has1990Data : true)
      .map(material => ({
        name: material.name,
        fullName: material.name,
        A1: use1990Data ? (material.A1_1990 || 0) : material.A1,
        A2: use1990Data ? (material.A2_1990 || 0) : material.A2,
        A3: use1990Data ? (material.A3_1990 || 0) : material.A3,
        A4: use1990Data ? (material.A4_1990 || 0) : (material.A4 || 0),
        total: use1990Data ? (material.total_1990 || 0) : material.total,
        location: material.location
      }));

    // Check if we have 1990 data when requested
    if (use1990Data && chartData.length === 0) {
      return (
        <Card className={fullHeight ? "h-full" : ""}>
          <div className="p-6 h-full flex flex-col">
            <h2 className="text-lg font-semibold mb-6">{title}</h2>
            <div className={`${fullHeight ? 'flex-1' : 'h-80'} flex items-center justify-center text-gray-500`}>
              <p>Keine 1990 CO₂e Daten verfügbar</p>
            </div>
          </div>
        </Card>
      );
    }
  }
  // Determine if X-axis labels need two lines (material + location)
  const labelsHaveSecondLine = (chartData || []).some(item =>
    (item.fullName || '').includes('\n') || (item.location && item.location.trim() !== '')
  );
  const xAxisHeight = labelsHaveSecondLine ? 64 : 28;
  const bottomMargin = labelsHaveSecondLine ? (fullHeight ? 14 : 8) : (fullHeight ? 0 : 4);


  // Custom tick component for two-line X-axis labels
  const CustomXAxisTick = ({ x, y, payload }) => {
    if (!payload || !payload.value) return null;

    // Find the corresponding material data
    const materialData = chartData.find(item => item.name === payload.value);
    if (!materialData) return null;

    // For combined view (1990 comparison), only show labels for current data (not 1990)
    // Keep them centered under their respective bars
    if (showCombinedData) {
      // Only show label for current data entries, skip 1990 entries
      if (materialData.type === '1990') {
        return null;
      }

      // Split the name by newline to get material and location
      const nameParts = materialData.fullName.split('\n');
      const materialName = nameParts[0] || '';
      const locationName = nameParts[1] || materialData.location || '';

      // Center the label under the current bar (same as default alignment)
      return (
        <g transform={`translate(${x},${y + 10})`}>
          <text x={0} y={0} dy={0} textAnchor="middle" fill="#666" fontSize="12">
            <tspan x={0} dy="0">{materialName}</tspan>
            <tspan x={0} dy="12">{locationName}</tspan>
          </text>
        </g>
      );
    }

    // Split the name by newline to get material and location
    const nameParts = materialData.fullName.split('\n');
    const materialName = nameParts[0] || '';
    const locationName = nameParts[1] || materialData.location || '';

    // Default centered alignment for non-combined views
    return (
      <g transform={`translate(${x},${y + 10})`}>
        <text x={0} y={0} dy={0} textAnchor="middle" fill="#666" fontSize="12">
          <tspan x={0} dy="0">{materialName}</tspan>
          <tspan x={0} dy="12">{locationName}</tspan>
        </text>
      </g>
    );
  };

  // Custom shape function to apply different colors based on data type
  const getBarColor = (dataKey, entry) => {
    if (!showCombinedData || !entry?.type) return getDefaultColor(dataKey);
    return entry.type === '1990' ? '#9ca3af' : getDefaultColor(dataKey);
  };

  const getDefaultColor = (dataKey) => {
    const colors = {
      A1: '#4ade80',
      A2: '#60a5fa',
      A3: '#a78bfa',
      A4: '#fb923c'
    };
    return colors[dataKey] || '#9ca3af';
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;

      // Split the fullName by newline to get material and location
      const nameParts = data.fullName.split('\n');
      const materialName = nameParts[0] || '';
      const locationName = nameParts[1] || data.location || '';

      return (
        <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold">{materialName}</p>
          <p className="text-sm text-gray-600 mb-2">{locationName}</p>
          <div className="space-y-1">
            <p className="text-green-600">A1: {(data.A1 || 0).toFixed(1).replace('.', ',')} kg CO₂e/t</p>
            <p className="text-blue-600">A2: {(data.A2 || 0).toFixed(1).replace('.', ',')} kg CO₂e/t</p>
            <p className="text-purple-600">A3: {(data.A3 || 0).toFixed(1).replace('.', ',')} kg CO₂e/t</p>
            <p className="text-orange-600">A4: {(data.A4 || 0).toFixed(1).replace('.', ',')} kg CO₂e/t</p>
            <p className="font-semibold border-t pt-1">Total: {(data.total || 0).toFixed(1).replace('.', ',')} kg CO₂e/t</p>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <Card className={fullHeight ? "h-full" : ""}>
      <div className={`p-6 ${fullHeight ? 'h-full flex flex-col' : ''}`}>
        <div className="mb-4 flex-shrink-0 flex justify-between items-center">
          <h2 className="text-lg font-semibold">{title}</h2>
          <div className="flex items-center gap-4">
            {/* Legend moved to header for testing */}
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-green-400 rounded-sm"></div>
                <span className="text-sm text-gray-600">A1 (Rohstoffe)</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-blue-400 rounded-sm"></div>
                <span className="text-sm text-gray-600">A2 (Transport)</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-purple-400 rounded-sm"></div>
                <span className="text-sm text-gray-600">A3 (Herstellung)</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-orange-400 rounded-sm"></div>
                <span className="text-sm text-gray-600">A4 (Transport Baustelle)</span>
              </div>
            </div>
            {customRightHeader ? (
              customRightHeader
            ) : (
              showToggleButton && (
                <button
                  onClick={onToggleView}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${isToggleActive
                    ? 'bg-green-500 text-white hover:bg-green-600'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  title={toggleLabel}
                >
                  {useIconToggle ? (
                    isToggleActive ? (
                      // Icon for Schicht-Ansicht (layers)
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                      </svg>
                    ) : (
                      // Icon for Projekt-Ansicht (project)
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    )
                  ) : (
                    toggleLabel
                  )}
                </button>
              )
            )}
          </div>
        </div>
        <div className={`${fullHeight ? 'flex-1 min-h-0' : 'h-96'}`}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{
                top: fullHeight ? 20 : 10,
                right: 20,
                left: 20,
                bottom: bottomMargin
              }}
              barGap={0}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="name"
                tick={<CustomXAxisTick />}
                interval={0}
                height={xAxisHeight}
                tickLine={false}
                axisLine={false}
                padding={{ left: 0, right: 0 }}
              />
              <YAxis
                label={{
                  value: 'kg CO₂e/t',
                  angle: -90,
                  position: 'insideLeft',
                  style: { textAnchor: 'middle' }
                }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar
                dataKey="A1"
                stackId="emissions"
                name="A1 (Rohstoffe)"
                fill="#4ade80"
                radius={[0, 0, 0, 0]}
                shape={(props) => {
                  const fillColor = props.payload?.type === '1990' ? '#86a891' : '#4ade80'; // Greyish green
                  return <rect {...props} fill={fillColor} />;
                }}
              />
              <Bar
                dataKey="A2"
                stackId="emissions"
                name="A2 (Transport)"
                fill="#60a5fa"
                radius={[0, 0, 0, 0]}
                shape={(props) => {
                  const fillColor = props.payload?.type === '1990' ? '#8ba3c7' : '#60a5fa'; // Greyish blue
                  return <rect {...props} fill={fillColor} />;
                }}
              />
              <Bar
                dataKey="A3"
                stackId="emissions"
                name="A3 (Herstellung)"
                fill="#a78bfa"
                radius={[0, 0, 0, 0]}
                shape={(props) => {
                  const fillColor = props.payload?.type === '1990' ? '#b5a3c7' : '#a78bfa'; // Greyish purple
                  return <rect {...props} fill={fillColor} />;
                }}
              />
              <Bar
                dataKey="A4"
                stackId="emissions"
                name="A4 (Transport Baustelle)"
                fill="#fb923c"
                radius={[4, 4, 0, 0]}
                shape={(props) => {
                  const { x, y, width, height } = props;
                  const fillColor = props.payload?.type === '1990' ? '#c7a891' : '#fb923c'; // Greyish orange
                  const radius = 4;

                  // Create path with rounded top corners only
                  const path = `
                    M ${x + radius} ${y}
                    L ${x + width - radius} ${y}
                    Q ${x + width} ${y} ${x + width} ${y + radius}
                    L ${x + width} ${y + height}
                    L ${x} ${y + height}
                    L ${x} ${y + radius}
                    Q ${x} ${y} ${x + radius} ${y}
                    Z
                  `;

                  return <path d={path} fill={fillColor} />;
                }}
              >
                <LabelList
                  dataKey="total"
                  position="top"
                  formatter={(value) => (value || 0).toFixed(1).replace('.', ',')}
                  style={{ fill: '#374151', fontSize: '14px', fontWeight: 600 }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

      </div>
    </Card>
  );
};



const CO2Visualization = ({ data, fullHeight = false, title = "Gesamt CO₂e", use1990Data = false, showCombinedData = false, showToggleButton = false, onToggleView, isToggleActive = false, toggleLabel = '1990 Vergleich', useIconToggle = false, customRightHeader = null }) => {
  // Always use the new AnalyticsChart component
  // This prevents the legacy CostChart with Plan/Ist labels from showing
  return (
    <div className={fullHeight ? "h-full" : "space-y-8"}>
      <AnalyticsChart
        data={data}
        fullHeight={fullHeight}
        title={title}
        use1990Data={use1990Data}
        showCombinedData={showCombinedData}
        showToggleButton={showToggleButton}
        onToggleView={onToggleView}
        isToggleActive={isToggleActive}
        toggleLabel={toggleLabel}
        useIconToggle={useIconToggle}
        customRightHeader={customRightHeader}
      />
    </div>
  );
};

export default CO2Visualization;